import { allowsConsumerBackSwipe } from "./consumer-history.ts"

/** A single edge gesture uses the same app back action as the header button. */
export const installConsumerEdgeBack = (enabled: () => boolean, back: () => boolean): (() => void) => {
  let gesture: { id: number; x: number; y: number; href: string; horizontal: boolean } | null = null
  const reset = () => {
    gesture = null
  }
  const allowed = () => enabled() && allowsConsumerBackSwipe(window.location.pathname) && document.querySelector("dialog[open], [aria-modal='true']") === null
  const start = (event: TouchEvent) => {
    reset()
    const touch = event.touches[0]
    if (!allowed() || event.touches.length !== 1 || touch === undefined || touch.clientX < 0 || touch.clientX > 24) return
    const target = event.target
    if (target instanceof Element && target.closest("input, textarea, select, [contenteditable]:not([contenteditable='false'])") !== null) return
    gesture = { id: touch.identifier, x: touch.clientX, y: touch.clientY, href: window.location.href, horizontal: false }
  }
  const move = (event: TouchEvent) => {
    if (gesture === null) return
    const touch = event.touches[0]
    if (event.touches.length !== 1 || touch?.identifier !== gesture.id || gesture.href !== window.location.href || !allowed()) return reset()
    const dx = touch.clientX - gesture.x
    const dy = Math.abs(touch.clientY - gesture.y)
    if (dx < -8 || (dy > 12 && dy > Math.max(dx, 0))) return reset()
    if (dx > 12 && dx > dy * 1.5) gesture.horizontal = true
    // Leave vertical scrolling untouched; claim only a confirmed edge-back pan.
    if (gesture.horizontal && event.cancelable) event.preventDefault()
  }
  const end = (event: TouchEvent) => {
    const current = gesture
    reset()
    if (current === null || !current.horizontal || event.touches.length !== 0 || current.href !== window.location.href || !allowed()) return
    const touch = Array.from(event.changedTouches).find(({ identifier }) => identifier === current.id)
    if (touch === undefined) return
    const dx = touch.clientX - current.x
    const dy = Math.abs(touch.clientY - current.y)
    const threshold = Math.max(64, Math.min(96, window.innerWidth * 0.2))
    if (dx >= threshold && dy < dx * 0.5) back()
  }
  window.addEventListener("touchstart", start, { passive: true })
  window.addEventListener("touchmove", move, { passive: false })
  window.addEventListener("touchend", end, { passive: true })
  window.addEventListener("touchcancel", reset)
  window.addEventListener("cashhub:history-changed", reset)
  window.addEventListener("pagehide", reset)
  document.addEventListener("visibilitychange", reset)
  return () => {
    reset()
    window.removeEventListener("touchstart", start)
    window.removeEventListener("touchmove", move)
    window.removeEventListener("touchend", end)
    window.removeEventListener("touchcancel", reset)
    window.removeEventListener("cashhub:history-changed", reset)
    window.removeEventListener("pagehide", reset)
    document.removeEventListener("visibilitychange", reset)
  }
}
